import client from "services/ngage/api-client";
import {DateTime} from "luxon";
import {AxiosRequestConfig, AxiosResponseTransformer} from "axios";
import {toSnakeCase} from "../string";

import type {
  EngagementInsightFilters,
  FeedbackInsightFilters,
  HardwareInsightFilters,
  InsightFilters,
  MarketingInsightFilters,
  OperatorEducationInsight,
  OperatorEngagementInsight,
  OperatorFeedbackInsight,
  OperatorHardwareInsight,
  OperatorMarketingInsight,
  OperatorSummaryInsight,
  OperatorTriggerInsight,
  OperatorUsersInsight,
  TimeSeriesCriteria,
  TriggerInsightFilters,
  UsersInsightFilters
} from "types/OperatorInsights";

// Types ----

export type InsightFormat = "json" | "csv"

export type GetInsightOptions = {
  name: string
  filters?: object
  format?: InsightFormat
  download?: boolean
  requestConfig?: AxiosRequestConfig
}

export type DataPoint = {
  name: string
  start: DateTime
  end: DateTime
  value: number
  increments: number
  decrements: number
  difference: number
}

export type TimeSeriesResource = {
  data: Array<DataPoint>
  totals: Record<string, number>
  averages: Record<string, number>
}

// Helpers ----

function serialiseFilters(filters: Partial<InsightFilters>) {
  const serialisedFilters: any = {...filters}

  const serialiseDateTime = (dateTime: DateTime) => dateTime
    .startOf('second')
    .toUTC()
    .toISO({suppressMilliseconds: true})

  if (DateTime.isDateTime(serialisedFilters.since)) {
    serialisedFilters.since = serialiseDateTime(serialisedFilters.since)
  }

  if (DateTime.isDateTime(serialisedFilters.until)) {
    serialisedFilters.until = serialiseDateTime(serialisedFilters.until)
  }

  return Object.fromEntries(
    Object.entries(serialisedFilters)
      .filter(([_key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => [toSnakeCase(key), value])
  )
}

async function getInsight<T extends object>({name, filters, format, download, requestConfig = {}}: GetInsightOptions) {
  const config: AxiosRequestConfig = {
    ...requestConfig,
    headers: {Accept: "application/json"},
    responseType: "json",
  };

  if (format === 'csv') {
    config.headers = {Accept: "text/csv"}
    config.responseType = "text"
  }

  if (!!download) {
    config.responseType = "blob"
  }

  return await client.get<T>(`/insights/${name}`, {
    params: filters ? serialiseFilters(filters) : null,
    ...config
  })
}

// Queries ----

async function timeSeries(criteria: TimeSeriesCriteria) {
  return (await client.get<TimeSeriesResource>('/time-series', {
    params: criteria,
    paramsSerializer: {
      serialize: criteria => {
        const stats = criteria.stats.join(',');
        const startAt = criteria.startAt.toUTC().toISO();
        const endAt = criteria.endAt.toUTC().toISO();

        return `stats=${stats}&start_at=${startAt}&end_at=${endAt}&group=${criteria.group}`
      }
    },

    transformResponse: (client.defaults.transformResponse as AxiosResponseTransformer[]).concat((data: any) => {
      data.data = data.data.map((point: any) => {
        point.start = DateTime.fromISO(point.start).toLocal();
        point.end = DateTime.fromISO(point.end).toLocal();

        return point;
      })

      return data
    })
  })).data
}

async function timeSeriesExport(criteria: TimeSeriesCriteria, format?: InsightFormat) {
  return (await client.get<Blob>('/time-series', {
    headers: {Accept: format === 'csv'? 'text/csv' : 'application/json'},
    responseType: "blob",
    params: criteria,
    paramsSerializer: {
      serialize: criteria => {
        const stats = criteria.stats.join(',');
        const startAt = criteria.startAt.toUTC().toISO();
        const endAt = criteria.endAt.toUTC().toISO();

        return `stats=${stats}&start_at=${startAt}&end_at=${endAt}&group=${criteria.group}`
      }
    },
  })).data
}

async function summary() {
  return (await client.get<OperatorSummaryInsight>('/insights')).data
}

async function users(filters: Partial<UsersInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorUsersInsight>({name: 'users', filters, requestConfig})).data
}

async function usersExport(filters: Partial<UsersInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'users', filters, format})).data
}

async function engagement(filters: Partial<EngagementInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorEngagementInsight>({name: 'engagement', filters, requestConfig})).data
}

async function engagementExport(filters?: Partial<EngagementInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'engagement', filters, format})).data
}

async function feedback(filters: Partial<FeedbackInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorFeedbackInsight>({name: 'feedback', filters, requestConfig})).data
}

async function feedbackExport(filters?: Partial<FeedbackInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'feedback', filters, format})).data
}

async function hardware(filters: Partial<HardwareInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorHardwareInsight>({name: 'hardware', filters, requestConfig})).data
}

async function hardwareExport(filters?: Partial<HardwareInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'hardware', filters, format})).data
}

async function triggers(filters: Partial<TriggerInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorTriggerInsight>({name: 'triggers', filters, requestConfig})).data
}

async function triggersExport(filters?: Partial<TriggerInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'triggers', filters, format})).data
}

async function marketing(filters: Partial<MarketingInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorMarketingInsight>({name: 'marketing', filters, requestConfig})).data
}

async function marketingExport(filters?: Partial<MarketingInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'marketing', filters, format})).data
}

async function education(filters: Partial<MarketingInsightFilters> = {}, requestConfig?: AxiosRequestConfig) {
  return (await getInsight<OperatorEducationInsight>({name: 'education', filters, requestConfig})).data
}

async function educationExport(filters?: Partial<MarketingInsightFilters>, format?: InsightFormat) {
  return (await getInsight<Blob>({name: 'education', filters, format})).data
}

const insights = {
  timeSeries,
  timeSeriesExport,
  summary,
  users,
  usersExport,
  engagement,
  engagementExport,
  feedback,
  feedbackExport,
  hardware,
  hardwareExport,
  triggers,
  triggersExport,
  marketing,
  marketingExport,
  education,
  educationExport,
}

export default insights